import { useNavigate } from "react-router";
import { useCatalogTests, useRuns } from "@/api/queries";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useTheme } from "@/lib/theme";
import { useFlags } from "../flags";
import { visibleNav } from "./nav";
import { useShellData } from "./shellData";

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const shell = useShellData();
  const enabled = useFlags();
  const { resolved, setPreference } = useTheme();
  const runs = useRuns({ limit: 5 }, open);
  const tests = useCatalogTests(open);
  const role = shell.user?.role;
  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };
  const canRun = role === "TESTER" || role === "ADMIN";
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Command palette" description="Search pages, runs and tests">
      <CommandInput placeholder="Search or jump to…" />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        <CommandGroup heading="Pages">
          {visibleNav(role, enabled).map((n) => (
            <CommandItem key={n.id} value={n.label} onSelect={() => go(n.to)}>
              <n.icon aria-hidden /> {n.label}
            </CommandItem>
          ))}
        </CommandGroup>
        {runs.data?.items.length ? (
          <CommandGroup heading="Recent runs">
            {runs.data.items.map((r) => (
              <CommandItem key={r.run_id} value={`${r.run_id} ${r.agent_artifact.version}`} onSelect={() => go(`/runs/${r.run_id}`)}>
                <span className="font-mono text-xs">{r.run_id}</span>
                <span className="ml-auto text-xs text-muted-foreground">nodejs {r.agent_artifact.version}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        {tests.data?.length ? (
          <CommandGroup heading="Tests">
            {tests.data.map((t) => (
              <CommandItem key={t.id} value={`${t.id} ${t.name}`} onSelect={() => go(`/catalog/${t.id}`)}>
                {t.name}
                <span className="ml-auto font-mono text-xs text-muted-foreground">{t.id}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        <CommandSeparator />
        <CommandGroup heading="Actions">
          {canRun && (
            <CommandItem value="New run" onSelect={() => go("/runs/new")}>
              New run
            </CommandItem>
          )}
          {canRun && (
            <CommandItem
              value="Run environment check"
              onSelect={() => {
                onOpenChange(false);
                shell.runEnvironmentCheck();
              }}
            >
              Run environment check
            </CommandItem>
          )}
          <CommandItem
            value="Toggle theme"
            onSelect={() => {
              setPreference(resolved === "dark" ? "light" : "dark");
              onOpenChange(false);
            }}
          >
            Toggle theme
          </CommandItem>
          {role === "ADMIN" && (
            <CommandItem value="Review waiting users" onSelect={() => go("/admin/users?tab=waiting")}>
              Review waiting users
            </CommandItem>
          )}
          <CommandItem
            value="Sign out"
            onSelect={() => {
              onOpenChange(false);
              shell.signOut();
            }}
          >
            Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
